import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Alert, Spinner } from "react-bootstrap";
import DeleteModal from "../../../components/DeleteModal";
import {
  useDeleteGeneralExpenseMutation,
  useGetAllGeneralExpenses,
} from "../../../../api/fleet-management/general-expenses/general-expenses.hooks";
import GeneralExpensesHeader from "./_components/GeneralExpensesHeader";
import GeneralExpensesTable from "./_components/GeneralExpensesTable";
import {
  filterGeneralExpenses,
  normalizeGeneralExpenses,
} from "./utils/general-expense.utils";

const GeneralExpenses = () => {
  const history = useHistory();
  const { data, isLoading, isError, error } = useGetAllGeneralExpenses();
  const { mutate: deleteGeneralExpense, isLoading: isDeleting } = useDeleteGeneralExpenseMutation();

  const [searchTerm, setSearchTerm] = useState("");
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);

  const expenses = normalizeGeneralExpenses(data);

  const filteredExpenses = useMemo(() => {
    return filterGeneralExpenses(expenses, searchTerm);
  }, [expenses, searchTerm]);

  const handleEdit = (generalExpenseId) => {
    history.push(`/fleet-management/general-expenses/update/${generalExpenseId}`);
  };

  const openDeleteModal = (expense) => {
    setSelectedExpense(expense);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setShowDeleteModal(false);
    setSelectedExpense(null);
  };

  const handleConfirmDelete = () => {
    if (!selectedExpense?._id) return;

    setFeedback({ type: "", message: "" });
    deleteGeneralExpense(selectedExpense._id, {
      onSuccess: () => {
        setFeedback({ type: "success", message: "General expense deleted successfully." });
        closeDeleteModal();
      },
      onError: (deleteError) => {
        setFeedback({
          type: "danger",
          message: deleteError?.response?.data?.message || "Failed to delete general expense.",
        });
      },
    });
  };

  if (isLoading) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body text-danger py-4">
            Failed to load general expenses. {error?.response?.data?.message || "Please try again."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <GeneralExpensesHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          total={filteredExpenses.length}
          onAddGeneralExpense={() => history.push("/fleet-management/general-expenses/add")}
        />

        <div className="card-body">
          {feedback.message && (
            <Alert variant={feedback.type} className="mb-3">
              {feedback.message}
            </Alert>
          )}

          {filteredExpenses.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted mb-0">No general expenses found</p>
            </div>
          ) : (
            <GeneralExpensesTable
              expenses={filteredExpenses}
              onEdit={handleEdit}
              onDelete={openDeleteModal}
              isDeleting={isDeleting}
            />
          )}
        </div>
      </div>

      <DeleteModal
        show={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Delete General Expense"
        message={`Are you sure you want to delete ${selectedExpense?.expenseType || "this expense"}? This action cannot be undone.`}
        confirmText="Delete Expense"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default GeneralExpenses;